import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Dropzone from 'react-dropzone'
import { connect } from 'react-redux'
import { withTranslation } from 'react-i18next'

import { ReactComponent as AddPhotoIcon } from '../../../assets/images/icons/add-photo-icon.svg'
import sampleImage1 from '../../../assets/images/sample-photos/Picverse-image-1.jpg'
import sampleImage1Thumb from '../../../assets/images/sample-photos/Picverse-image-1-s.jpg'
import sampleImage2 from '../../../assets/images/sample-photos/Picverse-image-2.jpg'
import sampleImage2Thumb from '../../../assets/images/sample-photos/Picverse-image-2-s.jpg'
import sampleImage3 from '../../../assets/images/sample-photos/Picverse-image-3.jpg'
import sampleImage3Thumb from '../../../assets/images/sample-photos/Picverse-image-3-s.jpg'

import {
  imageUploadAction,
  deleteImage,
  saveImageInfo,
  setSampleImage,
  setImageSrc,
  setImageError
} from '../../../store/actions/image.actions'
import { clearSettings } from '../../../store/actions/settings.actions'
import { showLoader, hideLoader } from '../../../store/actions/loader.actions'

import { getFilenameComponents } from '../../../helpers/util'

import imageUtils from '../../../helpers/flipRotate'

import Button from '../../Common/Buttons/Button/Button'
import Modal from '../../Common/Modal/Modal'

const formatsToConvert = ['.dpx','.pcx', '.pbm',  '.ppm', '.pgm', '.tiff', '.tga']
const acceptedFormats = ['.jpg', '.jpeg', '.png', '.bmp', '.xbm'].concat(formatsToConvert)

class Dropbox extends Component {
  static propTypes = {
    forwardDropzoneRef: PropTypes.func.isRequired,
    imageUpload: PropTypes.func.isRequired,
    imageSrc: PropTypes.string.isRequired,
    imageSrcUploaded: PropTypes.string.isRequired,
    isSyncError: PropTypes.bool.isRequired,
    clearSettings: PropTypes.func.isRequired,
    deleteImage: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    showLoader: PropTypes.func.isRequired,
    hideLoader: PropTypes.func.isRequired,
    openFileDialog: PropTypes.func.isRequired,
    saveImageSize: PropTypes.func.isRequired,
    dispatchSaveImageInfo: PropTypes.func.isRequired,
    dispatchSetSampleImage: PropTypes.func.isRequired,
    dispatchSetImageSrc: PropTypes.func.isRequired,
    dispatchSetImageError: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    i18n: PropTypes.any,
  }

  constructor(props) {
    super(props)

    this.state = {
      isFormatError: false,
      isRawFormatError: false,
      isFileSizeError: false,
      isImageSizeError: false
    }

    this.isUsingBackend = false
    this.imageInfo = null
    this.dropzoneRef = null
  }

  componentDidUpdate(prevPros) {
    const {
      imageSrcUploaded,
      isSyncError,
      hideLoader,
      dispatchSaveImageInfo,
      dispatchSetImageError,
      dispatchSetImageSrc
    } = this.props

    if (this.isUsingBackend) {
      if ((!prevPros.isSyncError && isSyncError)) {
        hideLoader()
        dispatchSetImageError(true)
      }

      if ((!prevPros.imageSrcUploaded && imageSrcUploaded)) {
        const {
          name,
          format,
          type
        } = this.imageInfo

        hideLoader()
        dispatchSaveImageInfo(name, format, type)
        dispatchSetImageSrc(imageSrcUploaded)
      }
    }
  }

  onDrop = (acceptedFiles, fileRejections, event) => {
    const {
      clearSettings,
      deleteImage, 
      saveImageSize,
    } = this.props

    this.isUsingBackend = false

    if (fileRejections.length) {
      const { format } = getFilenameComponents(fileRejections[0].name)

      if (format.toLowerCase() === 'raw') {
        this.setState({ isRawFormatError: true })
      } else {
        this.setState({ isFormatError: true })
      }

      return
    }

    const [acceptedFile] = acceptedFiles
    const {
      name,
      format
    } = getFilenameComponents(acceptedFile.name)
    const fileSize = (acceptedFile.size / (1024 * 1024)).toFixed(2)

    if (fileSize > 20) {
      this.setState({ isFileSizeError: true })
      clearSettings()
      deleteImage()
      return
    }

    const type = event.target.classList.contains('dropbox__container') ? 'Drag&Drop' : 'Button'

    saveImageSize(acceptedFile.size / 1024)

    this.imageInfo = {
      name,
      format,
      type
    }

    if (formatsToConvert.includes(`.${format}`)) {
      this.setURLUsingBackend(acceptedFile)
    } else {
      this.setURLUsingFileAPI(acceptedFile)
    }
  }

  setURLUsingBackend = (acceptedFile) => {
    const {
      clearSettings,
      deleteImage,
      showLoader,
      imageUpload,
      t
    } = this.props

    this.isUsingBackend = true

    clearSettings()
    deleteImage()

    showLoader(t('loader.upload-image'))
    // upload first, then use
    imageUpload(acceptedFile)
  }

  setURLUsingFileAPI = (acceptedFile) => {
    const {
      clearSettings,
      deleteImage,
      imageUpload,
      dispatchSaveImageInfo,
      dispatchSetImageSrc,
      dispatchSetImageError
    } = this.props

    const {
      name,
      format,
      type
    } = this.imageInfo

    imageUtils.blobToLoadedImage(acceptedFile).then((image) => {
      clearSettings()
      deleteImage()

      if (image.width > 10000 || image.height > 10000) {
        this.setState({
          isImageSizeError: true
        })

        return
      }

      dispatchSaveImageInfo(name, format, type)
      dispatchSetImageSrc(URL.createObjectURL(acceptedFile))
      imageUpload(acceptedFile)
    }, () => {
      clearSettings()
      deleteImage()

      dispatchSetImageError(true)
    })
  }

  onFormatModalClose = () => {
    this.setState({
      isFormatError: false,
      isRawFormatError: false
    })
  }

  onSizeModalClose = () => {
    this.setState({ isFileSizeError: false })
  }

  onSizeErrorModalClose = () => {
    const { hideLoader } = this.props
    hideLoader()

    this.setState({
      isImageSizeError: false
    })
  }

  tryAgain = () => {
    const {
      openFileDialog, 
      hideLoader 
    } = this.props
    
    hideLoader()
    openFileDialog()

    this.setState({
      isFormatError: false,
      isRawFormatError: false,
      isFileSizeError: false,
      isImageSizeError: false
    })
  }

  setDropzoneRef = (element) => {
    this.dropzoneRef = element
    this.props.forwardDropzoneRef(element)
  }

  onSampleClick = (e, name, src) => {
    const {
      imageUpload,
      dispatchSaveImageInfo,
      dispatchSetSampleImage
    } = this.props

    e.stopPropagation()

    dispatchSaveImageInfo(name, 'jpg', 'Sample')
    dispatchSetSampleImage(src)

    imageUtils.imageURLtoBlob(src).then((blob) => {
      imageUpload(new File([blob], '${name}.jpg', {
        type: 'image/jpeg'
      }))
    })
  }

  render() {
    const {
      isFormatError,
      isRawFormatError,
      isFileSizeError,
      isImageSizeError
    } = this.state
    const {
      imageSrc,
      loading,
      t
    } = this.props
    const shouldShowDropzoneContent = !loading &&
      !(isFormatError || isRawFormatError) &&
      !isFileSizeError &&
      !isImageSizeError

    return (
      <React.Fragment>
        <div className={`dropbox ${imageSrc ? 'dropbox_hidden' : ''}`}>
          <Dropzone
            ref={this.setDropzoneRef}
            onDrop={this.onDrop}
            accept={acceptedFormats}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <div
                {...getRootProps()}
                className="dropbox__container"
              >
                <input {...getInputProps()} />
                {shouldShowDropzoneContent && (
                  <>
                    <h1 className="dropbox__title">{t('dropbox.title')}</h1>
                    <div className="dropbox__inside-wrapper">
                      <AddPhotoIcon />
                      <div className="dropbox__inside-text">
                        {t('dropbox.drag-and-drop')}
                      </div>
                      <div className="dropbox__formats">{t('dropbox.formats')}</div>
                      <div className="dropbox__samples">
                        <div className="dropbox__samples-title">
                          {t('dropbox.our-photo')}
                        </div>
                        <div className="dropbox__samples-items">
                          <div
                            className="dropbox__samples-item"
                            onClick={(e) => {
                              this.onSampleClick(e, 'Picverse-image-1', sampleImage1)
                            }}
                          >
                            <img src={sampleImage1Thumb} alt="" />
                          </div>
                          <div
                            className="dropbox__samples-item"
                            onClick={(e) => {
                              this.onSampleClick(e, 'Picverse-image-2', sampleImage2)
                            }}
                          >
                            <img src={sampleImage2Thumb} alt="" />
                          </div>
                          <div
                            className="dropbox__samples-item"
                            onClick={(e) => {
                              this.onSampleClick(e, 'Picverse-image-3', sampleImage3)
                            }}
                          >
                            <img src={sampleImage3Thumb} alt="" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </Dropzone>
        </div>
        <Modal isOpen={isFormatError} onCloseModal={this.onFormatModalClose}>
          <div className="modal__body">{t('modals.image-format')}</div>
          <div className="modal__buttons">
            <Button onClick={this.tryAgain} variant="ghost">
              {t('buttons.open-photo')} 
            </Button>
          </div>
        </Modal>
        <Modal isOpen={isRawFormatError} onCloseModal={this.onFormatModalClose}>
          <div className="modal__body">{t('modals.image-raw-format')}</div>
          <div className="modal__buttons">
            <Button onClick={this.tryAgain} variant="ghost">
              {t('buttons.open-photo')}
            </Button>
          </div>
        </Modal>
        <Modal isOpen={isFileSizeError} onCloseModal={this.onSizeModalClose}>
          <div className="modal__body">
            <div className="modal__title">{t('modals.image-weight-error-title')}</div>
            {t('modals.image-weight-error-text')}
          </div>
          <div className="modal__buttons">
            <Button onClick={this.tryAgain} variant="ghost">
              {t('buttons.open-photo')}
            </Button>
          </div>
        </Modal>
        <Modal isOpen={isImageSizeError} onCloseModal={this.onSizeErrorModalClose}>
          <div className="modal__body">
            <div className="modal__title">{t('modals.image-size-error-title')}</div>
            {t('modals.image-size-error-text')}
          </div>
          <div className="modal__buttons">
            <Button variant="ghost" onClick={this.tryAgain}>
              {t('buttons.open-photo')}
            </Button>
          </div>
        </Modal>
      </React.Fragment>
    )
  }
}

const mapStateToProps = ({
  imageStore: { 
    imageSrc,
    imageSrcUploaded,
    isSyncError
  },
  loaderStore: { 
    loading,
  } 
}) => ({
  imageSrc,
  imageSrcUploaded,
  isSyncError,
  loading,
})

const mapDispatchToProps = (dispatch) => ({
  imageUpload: (image) => dispatch(imageUploadAction(image)),
  clearSettings: () => dispatch(clearSettings()),
  showLoader: (message) => dispatch(showLoader(message)),
  hideLoader: () => dispatch(hideLoader()),
  deleteImage: () => dispatch(deleteImage()),
  dispatchSaveImageInfo: (name, format, type) => dispatch(saveImageInfo(name, format, type)),
  dispatchSetSampleImage: (imageSrc) => dispatch(setSampleImage(imageSrc)),
  dispatchSetImageSrc: (imageSrc) => dispatch(setImageSrc(imageSrc)),
  dispatchSetImageError: (value) => dispatch(setImageError(value)),
})

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Dropbox)
)
